import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwbG9naXN0aWNzfGVufDB8fHx8MTc4NTYxMzQ3N3ww&ixlib=rb-4.1.0&q=85";

const EMPTY = { company_name: "", company_code: "", full_name: "", email: "", password: "", store_name: "", store_code: "" };

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok && res.pending) {
      setPending(true);
    } else if (res.ok) {
      toast.success(`Welcome to Trackboard, ${res.user.full_name}`);
      navigate("/admin");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
      <div className="absolute inset-0 bg-black/85" />
      <div className="relative z-10 w-full max-w-lg bg-card/90 backdrop-blur border border-border p-8">
        <div className="mb-6">
          <div className="bg-white rounded-lg px-5 py-4 flex items-center justify-center">
            <img src="/trackboard-logo.png" alt="Trackboard by LOM Group JA" className="w-56 h-auto" data-testid="register-logo" />
          </div>
        </div>
        <h1 className="font-display text-2xl font-bold mb-1">Create your company account</h1>
        <p className="text-sm text-muted-foreground mb-2">Set up Trackboard for your courier business in under a minute.</p>
        {!pending && (
          <button type="button" onClick={() => navigate("/pricing")} className="text-sm text-muted-foreground hover:text-white hover:underline mb-4" data-testid="reg-pricing-link">
            See pricing →
          </button>
        )}
        {pending ? (
          <div className="text-center py-6" data-testid="register-pending">
            <CheckCircle2 size={56} className="text-alert-green mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold mb-2">Account submitted</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Thanks for signing up! Your account is <b>pending approval</b>. We'll email you at{" "}
              <span className="text-white">{form.email}</span> as soon as it's activated, then you can sign in.
            </p>
            <Button onClick={() => navigate("/login")} className="w-full h-11 text-base font-semibold" data-testid="pending-go-login">
              Back to sign in
            </Button>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Company Name</label>
              <Input className="mt-1 h-11" value={form.company_name} onChange={set("company_name")} required placeholder="Island Express Courier" data-testid="reg-company-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Company Code</label>
              <Input className="mt-1 h-11" value={form.company_code} onChange={set("company_code")} required placeholder="IEC" maxLength={8} data-testid="reg-company-code" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Your Full Name</label>
            <Input className="mt-1 h-11" value={form.full_name} onChange={set("full_name")} required placeholder="Jane Doe" data-testid="reg-full-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Work Email</label>
            <Input type="email" className="mt-1 h-11" value={form.email} onChange={set("email")} required placeholder="you@company.com" data-testid="reg-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Password</label>
            <Input type="password" className="mt-1 h-11" value={form.password} onChange={set("password")} required placeholder="At least 8 characters" data-testid="reg-password" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">First Store Name</label>
              <Input className="mt-1 h-11" value={form.store_name} onChange={set("store_name")} placeholder="Main Branch" data-testid="reg-store-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">First Store Code</label>
              <Input className="mt-1 h-11" value={form.store_code} onChange={set("store_code")} placeholder="MAIN" maxLength={8} data-testid="reg-store-code" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11 text-base font-semibold" data-testid="register-submit">
            {loading ? <Loader2 className="animate-spin" /> : "Create Account"}
          </Button>
        </form>
        )}
        {!pending && (
        <div className="mt-4 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <button type="button" onClick={() => navigate("/login")} className="text-white font-semibold hover:underline" data-testid="go-login-link">
            Sign in
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
